/**
 * Kit expansion and quote pricing, on the device.
 *
 * A port of `app/services/pricing.py`. Both are held to the same cases in
 * `tests/fixtures/costing_cases.json` — see the note in `costing.ts` for why
 * duplicating rules in two languages is survivable only because of that.
 *
 * This is the half that lets a tech build a priced quote at a site walk with
 * no signal, which is the feature the kit previewer exists for: spin the
 * camera count and watch the cable, the labour and the EWP days move with it.
 */

import { cents, marginBp } from "./costing";

/* ------------------------------------------------- the quantity formula */

export class FormulaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FormulaError";
  }
}

type Token = { kind: "num"; value: number } | { kind: "name"; value: string } | { kind: "op"; value: string };

const OPERATORS = ["**", "//", "+", "-", "*", "/", "%", "(", ")", ","];

function tokenise(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < source.length) {
    const ch = source[i]!;

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      const start = i;
      while (i < source.length && /[0-9._]/.test(source[i]!)) i += 1;
      const text = source.slice(start, i).replace(/_/g, "");
      const value = Number(text);
      if (!Number.isFinite(value)) throw new FormulaError(`${text} is not a number`);
      tokens.push({ kind: "num", value });
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      const start = i;
      while (i < source.length && /[A-Za-z0-9_]/.test(source[i]!)) i += 1;
      tokens.push({ kind: "name", value: source.slice(start, i) });
      continue;
    }

    const two = source.slice(i, i + 2);
    if (OPERATORS.includes(two)) {
      tokens.push({ kind: "op", value: two });
      i += 2;
      continue;
    }
    if (OPERATORS.includes(ch)) {
      tokens.push({ kind: "op", value: ch });
      i += 1;
      continue;
    }

    throw new FormulaError(`${ch} is not allowed in a quantity formula`);
  }

  return tokens;
}

/** Half away from zero, matching `cents` — not JavaScript's `Math.round`. */
function roundHalfUp(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value)) || 0;
}

const FUNCS: Record<string, (...args: number[]) => number> = {
  ceil: Math.ceil,
  floor: Math.floor,
  round: (x) => roundHalfUp(x ?? 0),
  max: (...xs) => Math.max(...xs),
  min: (...xs) => Math.min(...xs),
  abs: (x) => Math.abs(x ?? 0),
};

/**
 * Evaluate a kit line's quantity expression against the driver quantity `n`.
 *
 * Supported: numbers, the name `n`, `+ - * / // % **`, unary minus, and the
 * functions ceil, floor, round, max, min and abs. Everything else is rejected.
 *
 * A hand-written parser rather than `new Function` or `eval`, for exactly the
 * reason the Python side walks an AST rather than calling `eval` with stripped
 * globals: these formulas are user data, and an evaluator that can reach
 * anything outside this list is an arbitrary-code hole waiting to be found.
 * A whitelist cannot be talked around; a sandbox can.
 */
export function evaluateQty(formula: string, n: number): number {
  const tokens = tokenise(formula);
  let pos = 0;

  const peek = (): Token | undefined => tokens[pos];
  const isOp = (value: string) => {
    const token = peek();
    return token?.kind === "op" && token.value === value;
  };
  const eat = (value: string) => {
    if (!isOp(value)) throw new FormulaError(`expected ${value} in quantity formula`);
    pos += 1;
  };

  // expr := term (('+'|'-') term)*
  function expr(): number {
    let left = term();
    for (;;) {
      if (isOp("+")) {
        pos += 1;
        left += term();
      } else if (isOp("-")) {
        pos += 1;
        left -= term();
      } else {
        return left;
      }
    }
  }

  // term := unary (('*'|'/'|'//'|'%') unary)*
  function term(): number {
    let left = unary();
    for (;;) {
      const token = peek();
      if (token?.kind !== "op") return left;

      if (token.value === "*") {
        pos += 1;
        left *= unary();
      } else if (token.value === "/" || token.value === "//" || token.value === "%") {
        const op = token.value;
        pos += 1;
        const right = unary();
        if (right === 0) throw new FormulaError("division by zero in quantity formula");
        if (op === "/") left = left / right;
        // Python floors toward negative infinity and takes the divisor's sign
        // for `%`; JavaScript truncates and takes the dividend's. On positive
        // quantities they agree, but a formula is user data and the two
        // implementations have to give the same answer on every input.
        else if (op === "//") left = Math.floor(left / right);
        else left = ((left % right) + right) % right;
      } else {
        return left;
      }
    }
  }

  // unary := ('+'|'-') unary | power
  function unary(): number {
    if (isOp("-")) {
      pos += 1;
      return -unary();
    }
    if (isOp("+")) {
      pos += 1;
      return unary();
    }
    return power();
  }

  // power := atom ('**' unary)?   — right associative, and `**` binds tighter
  // than a unary minus on its left, so -2**2 is -4 as it is in Python.
  function power(): number {
    const base = atom();
    if (isOp("**")) {
      pos += 1;
      return base ** unary();
    }
    return base;
  }

  function atom(): number {
    const token = peek();
    if (token === undefined) throw new FormulaError("quantity formula ended unexpectedly");

    if (token.kind === "num") {
      pos += 1;
      return token.value;
    }

    if (token.kind === "name") {
      pos += 1;
      if (isOp("(")) {
        const fn = FUNCS[token.value];
        if (!fn) throw new FormulaError("only ceil, floor, round, max, min and abs may be called");
        eat("(");
        const args: number[] = [];
        if (!isOp(")")) {
          args.push(expr());
          while (isOp(",")) {
            pos += 1;
            args.push(expr());
          }
        }
        eat(")");
        return fn(...args);
      }
      if (token.value !== "n") {
        throw new FormulaError(`unknown name '${token.value}'; only 'n' is available`);
      }
      return n;
    }

    if (token.value === "(") {
      eat("(");
      const value = expr();
      eat(")");
      return value;
    }

    throw new FormulaError(`${token.value} is not allowed in a quantity formula`);
  }

  const result = expr();
  if (pos !== tokens.length) throw new FormulaError(`unexpected trailing input in ${JSON.stringify(formula)}`);
  if (!Number.isFinite(result)) throw new FormulaError("quantity formula did not produce a number");
  return result;
}

/* ----------------------------------------------------------------- margin */

/**
 * Price to hit a *margin* on cost, not a markup.
 *
 * margin = (sell - cost) / sell, so sell = cost / (1 - margin).
 *
 * A 40% markup on $100 is $140 and yields a 28.6% margin. This is the most
 * common estimating error in the trade and it compounds across every line.
 */
export function sellFromCost(costCents: number, margin_bp: number): number {
  if (margin_bp >= 10_000) throw new RangeError("margin must be below 100%");
  if (margin_bp <= 0) return costCents;
  return cents(costCents / ((10_000 - margin_bp) / 10_000));
}

/** What a markup on cost actually produces as a margin. */
export function markupBpToMarginBp(markup_bp: number): number {
  return cents((markup_bp / (10_000 + markup_bp)) * 10_000);
}

export { marginBp };

/* -------------------------------------------------------------- expansion */

export type ItemSpec = {
  id: string;
  description: string;
  kind?: string;
  uom?: string;
  cost_cents?: number;
  sell_cents?: number;
  /** When set, sell is derived from cost rather than taken from `sell_cents`. */
  margin_bp?: number | null;
  creates_asset?: boolean;
};

export type KitLineSpec = {
  item: ItemSpec;
  qty_formula?: string;
  include_by_default?: boolean;
  is_optional?: boolean;
  sort?: number;
  note?: string | null;
};

export type KitSpec = {
  code: string;
  name: string;
  lines: KitLineSpec[];
  driver_label?: string;
};

export type ExpandedLine = {
  item_id: string;
  description: string;
  kind: string;
  uom: string;
  qty: number;
  unit_cost_cents: number;
  unit_sell_cents: number;
  creates_asset: boolean;
  note: string | null;
  cost_cents: number;
  sell_cents: number;
};

export type ExpandedSection = {
  kit_code: string;
  title: string;
  driver_qty: number;
  lines: ExpandedLine[];
  cost_cents: number;
  sell_cents: number;
};

/** Three decimal places, matching the Numeric(12, 3) the server stores. */
function round3(value: number): number {
  return Math.sign(value) * (Math.round(Math.abs(value) * 1000) / 1000) || 0;
}

/**
 * Turn one kit plus a driver quantity into priced lines.
 *
 * `includeOptional` is the set of item ids the tech ticked on; optional lines
 * not in it are dropped. So are lines whose formula comes out at zero or less
 * — that is how "one EWP day per 4 cameras, but none on an indoor job"
 * expresses itself without a special case anywhere.
 */
export function expandKit(
  kit: KitSpec,
  driverQty: number,
  options: { includeOptional?: Set<string>; defaultMarginBp?: number | null } = {},
): ExpandedSection {
  const includeOptional = options.includeOptional ?? new Set<string>();
  const defaultMarginBp = options.defaultMarginBp ?? null;
  const lines: ExpandedLine[] = [];

  const ordered = [...kit.lines].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  for (const line of ordered) {
    const item = line.item;
    const isOptional = line.is_optional === true;

    if (isOptional && !includeOptional.has(item.id)) continue;
    if (!isOptional && line.include_by_default === false) continue;

    const qty = evaluateQty(line.qty_formula ?? "n", driverQty);
    if (qty <= 0) continue;

    const cost = item.cost_cents ?? 0;
    let unitSell: number;
    if (item.margin_bp !== null && item.margin_bp !== undefined) unitSell = sellFromCost(cost, item.margin_bp);
    else if (item.sell_cents) unitSell = item.sell_cents;
    else if (defaultMarginBp !== null) unitSell = sellFromCost(cost, defaultMarginBp);
    else unitSell = cost;

    const rounded = round3(qty);
    lines.push({
      item_id: item.id,
      description: item.description,
      kind: item.kind ?? "part",
      uom: item.uom ?? "ea",
      qty: rounded,
      unit_cost_cents: cost,
      unit_sell_cents: unitSell,
      creates_asset: item.creates_asset === true,
      note: line.note ?? null,
      cost_cents: cents(cost * rounded),
      sell_cents: cents(unitSell * rounded),
    });
  }

  return {
    kit_code: kit.code,
    title: kit.name,
    driver_qty: driverQty,
    lines,
    cost_cents: lines.reduce((sum, l) => sum + l.cost_cents, 0),
    sell_cents: lines.reduce((sum, l) => sum + l.sell_cents, 0),
  };
}

/* ---------------------------------------------------------------- totals */

export type QuoteTotals = {
  cost_cents: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  margin_cents: number;
  margin_bp: number;
};

/**
 * Total a quote.
 *
 * With `targetMarginBp` the sell side is *re-derived* from total cost, so
 * dragging the margin slider reprices the whole job coherently rather than
 * scaling line prices that were each individually correct.
 */
export function priceQuote(
  sections: { cost_cents: number; sell_cents: number }[],
  options: { targetMarginBp?: number | null; taxRateBp?: number } = {},
): QuoteTotals {
  const { targetMarginBp = null, taxRateBp = 1000 } = options;

  const cost = sections.reduce((sum, s) => sum + s.cost_cents, 0);
  const subtotal =
    targetMarginBp !== null ? sellFromCost(cost, targetMarginBp) : sections.reduce((sum, s) => sum + s.sell_cents, 0);
  const tax = cents((subtotal * taxRateBp) / 10_000);

  return {
    cost_cents: cost,
    subtotal_cents: subtotal,
    tax_cents: tax,
    total_cents: subtotal + tax,
    margin_cents: subtotal - cost,
    margin_bp: marginBp(cost, subtotal),
  };
}

/**
 * Split a total across weights so the parts sum *exactly* to the total.
 *
 * Largest-remainder. Rounding each share on its own leaves a cent adrift, and
 * a quote whose sections do not add up to its total is a support ticket.
 */
export function apportion(totalCents: number, weights: number[]): number[] {
  if (weights.length === 0) return [];

  const weightSum = weights.reduce((a, b) => a + b, 0);
  if (weightSum === 0) {
    const out = new Array<number>(weights.length).fill(0);
    out[0] = totalCents;
    return out;
  }

  const exact = weights.map((w) => (totalCents * w) / weightSum);
  // Truncate toward zero, as Python's `int()` does, rather than Math.floor —
  // they differ on negatives, and a credit note is a negative total.
  const floors = exact.map((e) => Math.trunc(e));
  const remainder = totalCents - floors.reduce((a, b) => a + b, 0);

  // The remainder is negative when the total is. Truncation toward zero leaves
  // the parts summing *above* the total then, so the spare cent has to be
  // deducted rather than added, and from the smallest fractional parts rather
  // than the largest. Both implementations had this wrong in the same way
  // until the shared fixture asked them about a credit note.
  const step = remainder >= 0 ? 1 : -1;

  // Stable sort: ties keep their original order, so both languages hand the
  // odd cent to the same section.
  const order = exact
    .map((e, i) => ({ i, rest: e - floors[i]! }))
    .sort((a, b) => (step === 1 ? b.rest - a.rest : a.rest - b.rest) || a.i - b.i)
    .map((x) => x.i);

  for (let k = 0; k < Math.abs(remainder); k += 1) {
    const index = order[k % order.length]!;
    floors[index] = floors[index]! + step;
  }
  return floors;
}
